import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardFooter, 
  CardHeader, 
  CardTitle 
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { UserAvatar } from "@/components/UserAvatar";
import { Badge } from "@/components/ui/badge";
import { useServices, Service } from "@/hooks/useServices";
import { useAuth } from "@/contexts/AuthContext";
import { Pencil, Trash2, Briefcase } from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useServiceUpdates } from "@/hooks/useServiceUpdates";
import { useUserProfiles } from "@/hooks/useUserProfiles";
import MyRequestsList from "@/components/MyRequestsList";

// Category icons map matching DiscoverServices
const categoryIcons: Record<string, React.FC<{ className?: string }>> = {
  "Web Development": Briefcase,
  "Design": Briefcase,
  "Writing": Briefcase,
  "Marketing and Sales": Briefcase,
  "Game Development": Briefcase,
  "Tutoring": Briefcase,
  "Photography": Briefcase,
  "Music": Briefcase,
  "Language": Briefcase,
  "Health": Briefcase,
  "Fashion": Briefcase,
  "Business": Briefcase,
  "Arts": Briefcase,
  "Engineering and CAD": Briefcase,
  "Other": Briefcase
};

// Category colors matching DiscoverServices
const categoryColors: Record<string, string> = {
  "Web Development": "bg-blue-500 text-white",
  "Design": "bg-pink-500 text-white",
  "Writing": "bg-green-500 text-white",
  "Marketing and Sales": "bg-orange-500 text-white",
  "Game Development": "bg-purple-500 text-white",
  "Tutoring": "bg-amber-500 text-white",
  "Photography": "bg-indigo-500 text-white",
  "Music": "bg-red-500 text-white",
  "Language": "bg-emerald-500 text-white",
  "Health": "bg-rose-500 text-white",
  "Fashion": "bg-fuchsia-500 text-white",
  "Business": "bg-cyan-500 text-white",
  "Arts": "bg-violet-500 text-white",
  "Engineering and CAD": "bg-slate-500 text-white",
  "Other": "bg-gray-500 text-white"
};

const MyServices = () => {
  const { user, loading: authLoading } = useAuth();
  const { getUserServices, deleteService, isLoading, categories } = useServices();
  const { getUserProfile } = useUserProfiles();
  const [services, setServices] = useState<Service[]>([]);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [serviceToDelete, setServiceToDelete] = useState<string | null>(null);
  const [userProfiles, setUserProfiles] = useState<Record<string, any>>({});
  const { serviceVersion } = useServiceUpdates();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const activeTab = searchParams.get('tab') || 'services';

  const loadServices = async () => {
    if (!user) return;
    
    try {
      const userServices = await getUserServices(user.id);
      setServices(userServices);
      
      // Load user profile for the service owner (current user)
      if (userServices.length > 0) {
        try {
          const profile = await getUserProfile(user.id);
          setUserProfiles({ [user.id]: profile });
        } catch (error) {
          console.error(`Error loading profile for ${user.id}:`, error);
        }
      }
    } catch (error) {
      console.error("Error loading services:", error);
      toast.error("Failed to load your services");
    }
  };

  useEffect(() => {
    // Redirect if not logged in
    if (!user && !authLoading) {
      toast.error("You must be logged in to view this page");
      navigate("/auth?mode=login");
      return;
    }

    // Fetch user services if logged in
    if (user) {
      loadServices();
    }
  }, [user, authLoading, serviceVersion]); // Added serviceVersion to dependencies

  const handleDeleteConfirmation = (serviceId: string) => {
    setServiceToDelete(serviceId);
    setIsDeleteDialogOpen(true);
  };

  const handleConfirmedDelete = async () => {
    if (!serviceToDelete) return;
    
    try {
      await deleteService(serviceToDelete);
      toast.success("Service deleted successfully");
      // Refresh the services list
      loadServices();
    } catch (error) {
      console.error("Error deleting service:", error);
      toast.error("Failed to delete service");
    } finally {
      // Reset delete state
      setIsDeleteDialogOpen(false);
      setServiceToDelete(null);
    }
  };

  const handleEdit = (serviceId: string) => {
    // Navigate to edit page with the service ID
    navigate(`/edit-service/${serviceId}`);
  };

  const getCategoryNameById = (id: string | null) => {
    if (!id) return "Uncategorized";
    const category = categories?.find(cat => cat.id === id);
    return category ? category.name : "Uncategorized";
  };

  const shortenDescription = (description: string, maxLength: number = 150) => {
    if (description.length <= maxLength) return description;
    return description.substring(0, maxLength).trim() + '...';
  };

  if (authLoading || isLoading) {
    return (
      <div className="container mx-auto p-6 flex justify-center items-center min-h-[50vh]">
        <div className="text-center">
          <div className="animate-spin w-12 h-12 border-4 border-anyskill-purple border-t-transparent rounded-full mx-auto"></div>
          <p className="mt-4 text-lg text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold">My Services and Requests</h1>
        <p className="text-muted-foreground mt-2">Manage the services you offer and requests you've made</p>
      </div>

      <Tabs value={activeTab} onValueChange={(value) => navigate(`/my-services?tab=${value}`)} className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="services">My Services</TabsTrigger>
          <TabsTrigger value="requests">My Requests</TabsTrigger>
        </TabsList>
        
        <TabsContent value="services" className="mt-6">
          <div className="flex justify-end mb-4">
            <Button 
              className="bg-anyskill-purple hover:bg-anyskill-purple/90"
              onClick={() => navigate("/offer-service")}
            >
              + Post New Service
            </Button>
          </div>
          {services.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <h2 className="text-xl font-medium text-muted-foreground">You haven't created any services yet</h2>
              <p className="text-muted-foreground mt-2">Create your first service to start offering your skills</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {services.map((service) => {
                const categoryName = getCategoryNameById(service.category_id);
                const userProfile = userProfiles[service.user_id] || { 
                  name: "Unknown User", 
                  avatarInitials: "UN",
                  avatarUrl: undefined 
                };
                const IconComponent = categoryIcons[categoryName] || Briefcase;
                const colorClass = categoryColors[categoryName] || "bg-gray-500 text-white";
                
                return (
                  <Card key={service.id} className="overflow-hidden hover:shadow-md transition-shadow relative border-2 border-green-200">
                    {/* Service Badge */}
                    <div className="absolute top-2 left-2 z-10">
                      <Badge variant="secondary" className="bg-green-500 text-white text-xs font-medium px-2 py-1">
                        SERVICE
                      </Badge>
                    </div>

                    {/* Category Icon Area - matching service card design */}
                    <div className="aspect-[3/1] bg-gray-100 dark:bg-gray-700 relative overflow-hidden">
                      {service.image_url ? (
                        <>
                          <img 
                            src={service.image_url} 
                            alt={service.title}
                            className="w-full h-full object-cover"
                          />
                          <div className="absolute top-2 right-2">
                            <div className={`${colorClass} p-1.5 rounded-full shadow-lg border-2 border-white backdrop-blur-sm bg-opacity-90`}>
                              <IconComponent className="h-4 w-4" />
                            </div>
                          </div>
                        </>
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <div className={`${colorClass} p-2 rounded-full`}>
                            <IconComponent className="h-6 w-6" />
                          </div>
                        </div>
                      )}
                    </div>
                    
                    <CardContent className="p-4">
                      {/* User Profile Section - matching service card design */}
                      <div className="flex items-center mb-3">
                        <UserAvatar
                          userId={user?.id}
                          avatarUrl={userProfile.avatarUrl}
                          fallback={userProfile.avatarInitials}
                          size="sm"
                          className="mr-2"
                        />
                        <span className="text-sm text-muted-foreground truncate">{userProfile.name}</span>
                      </div>
                      
                      {/* Title - matching service card design */}
                      <CardTitle className="text-base mb-3 line-clamp-2 leading-tight">{service.title}</CardTitle>
                      
                      {/* Description - matching service card design */}
                      <p className="text-sm text-muted-foreground mb-3 line-clamp-3">
                        {shortenDescription(service.description || "No description provided")}
                      </p>
                      
                      {/* Pricing and Action Buttons Section - matching service card design */}
                      <div className="flex justify-between items-end">
                        <div className="flex flex-col">
                          <div className="flex items-center">
                            <span className="font-semibold text-base">${service.price}</span>
                          </div>
                        </div>
                        
                        {/* Action Buttons - matching service card design */}
                        <div className="flex gap-2 flex-wrap">
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={() => handleEdit(service.id)}
                            className="flex items-center gap-1 text-xs px-3 py-2 h-8"
                          >
                            <Pencil size={14} />
                            <span className="hidden sm:inline">Edit</span>
                          </Button>
                          <Button 
                            variant="destructive" 
                            size="sm" 
                            onClick={() => handleDeleteConfirmation(service.id)}
                            className="flex items-center gap-1 text-xs px-3 py-2 h-8"
                          >
                            <Trash2 size={14} />
                            <span className="hidden sm:inline">Delete</span>
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>
        
        <TabsContent value="requests" className="mt-6">
          <MyRequestsList />
        </TabsContent>
      </Tabs>
      
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure you want to delete this service?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete your service
              and remove it from our servers.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmedDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default MyServices;
